import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { updateUserPassword } from '../../context/user/UserActions';
import { useAuth } from '../../context/AuthContext';

export default function PasswordDialog({ open, onClose, userId, onSuccess }){
  const { user: authUser, hasRole } = useAuth();
  const isAdmin = hasRole && hasRole('ADMIN');
  const targetIsSelf = authUser && String(authUser.id) === String(userId);
  // Show currentPassword when user is changing their own password and not an admin resetting someone else
  const requireCurrent = !isAdmin || targetIsSelf;

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!open) return null;

  const initialValues = {
    oldPassword: '',
    newPassword: '',
    repeatNewPassword: ''
  };

  const validationSchema = Yup.object({
    oldPassword: requireCurrent ? Yup.string().min(6,'Enter current password').required('Current password required') : Yup.string(),
    newPassword: Yup.string().min(6,'Password must be at least 6 characters').required('Please provide new password'),
    repeatNewPassword: Yup.string().oneOf([Yup.ref('newPassword')], 'Passwords must match').required('Please repeat new password')
  });

  return (
    <dialog open className="modal">
      <div className="modal-box w-11/12 max-w-lg">
        <h3 className="font-bold text-lg mb-4">Change Password</h3>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, actions) => {
            actions.setSubmitting(true);
            setErrorMessage('');
            setSuccessMessage('');
            try{
              const payload = { newPassword: values.newPassword };
              if (requireCurrent) payload.oldPassword = values.oldPassword;
              const res = await updateUserPassword(userId, payload);
              if (res?.error) {
                setErrorMessage(res.error || 'Failed to update password');
              } else {
                setSuccessMessage('Password updated');
                onSuccess && onSuccess();
                // close after short delay to let user read message
                setTimeout(() => { onClose && onClose(); }, 700);
              }
            }catch(e){
              setErrorMessage(e?.message || 'Network error');
            }finally{
              actions.setSubmitting(false);
            }
          }}
        >
          {formik => (
            <Form>
              <div className="grid gap-3">
                {requireCurrent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current password</label>
                    <Field name="oldPassword" type="password" className="w-full px-3 py-2 border rounded" />
                    <ErrorMessage name="oldPassword" component="div" className="text-red-600 text-sm" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">New password</label>
                  <Field name="newPassword" type="password" className="w-full px-3 py-2 border rounded" />
                  <ErrorMessage name="newPassword" component="div" className="text-red-600 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Repeat new password</label>
                  <Field name="repeatNewPassword" type="password" className="w-full px-3 py-2 border rounded" />
                  <ErrorMessage name="repeatNewPassword" component="div" className="text-red-600 text-sm" />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>Update</button>
              </div>

              {errorMessage && <div className="mt-3 text-red-600 text-sm">{errorMessage}</div>}
              {successMessage && <div className="mt-3 text-green-600 text-sm">{successMessage}</div>}
            </Form>
          )}
        </Formik>
      </div>
    </dialog>
  );
}
